<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">Tableau de bord</h1>
          <small class="text-muted">
            Vue globale ({{ periodLabel }})
            <span v-if="rangeLabel"> · {{ rangeLabel }}</span>
          </small>
        </div>

        <!-- Sélecteur de période -->
        <div class="d-flex align-items-center gap-2">
          <label class="small mb-0">Période:</label>
          <select
            v-model="period"
            class="form-select form-select-sm"
            @change="fetchSummary"
          >
            <option value="day">Aujourd’hui</option>
            <option value="week">7 derniers jours</option>
            <option value="month">30 derniers jours</option>
          </select>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger mb-3 py-2">
        {{ errorMessage }}
      </div>

      <!-- Cards résumé -->
      <div class="row g-3 mb-3">
        <!-- Produits -->
        <div class="col-xl-3 col-md-6">
          <RouterLink
            :to="{ name: 'Products' }"
            class="card shadow-sm dashboard-card dashboard-summary-link h-100"
            aria-label="Voir les produits"
          >
            <div class="card-body">
              <div class="dashboard-card-content">
                <div class="position-relative">
                  <div class="small text-muted">Produits (total)</div>
                  <div class="h4 mb-0">{{ totals.products_count }}</div>
                  <div class="small text-muted">
                    Actifs: <strong>{{ totals.products_active_count }}</strong>
                    · Inactifs: <strong>{{ totals.products_inactive_count }}</strong>
                  </div>
                </div>
                <div class="dashboard-icon bg-primary-subtle text-primary">
                  <i class="fa fa-boxes"></i>
                </div>
              </div>

              <hr class="my-2" />

              <div class="d-flex justify-content-between small">
                <span class="text-muted">Qté stock</span>
                <strong>{{ totals.stock_quantity_sum }}</strong>
              </div>

              <div class="d-flex justify-content-between small mt-1">
                <span class="text-muted">Valeur stock</span>
                <strong>{{ $format.currency(totals.stock_value_sum) }}</strong>
              </div>
            </div>
          </RouterLink>
        </div>

        <!-- Alertes stock -->
        <div class="col-xl-3 col-md-6">
          <RouterLink
            :to="{ name: 'Products' }"
            class="card shadow-sm dashboard-card dashboard-summary-link h-100"
            aria-label="Voir les produits concernés par les alertes de stock"
          >
            <div class="card-body">
              <div class="dashboard-card-content">
                <div class="position-relative">
                  <div class="small text-muted">Alertes stock</div>
                  <div class="h4 mb-0">{{ totals.out_of_stock_count + totals.below_min_stock_count }}</div>
                  <div class="small text-muted">
                    À surveiller rapidement
                  </div>
                </div>
                <div class="dashboard-icon bg-danger-subtle text-danger">
                  <i class="fa fa-triangle-exclamation"></i>
                </div>
              </div>

              <hr class="my-2" />

              <div class="d-flex justify-content-between small">
                <span class="text-muted">Rupture</span>
                <strong>{{ totals.out_of_stock_count }}</strong>
              </div>
              <div class="d-flex justify-content-between small mt-1">
                <span class="text-muted">Sous min</span>
                <strong>{{ totals.below_min_stock_count }}</strong>
              </div>
            </div>
          </RouterLink>
        </div>

        <!-- Ventes aujourd'hui -->
        <div class="col-xl-3 col-md-6">
          <RouterLink
            :to="{ name: 'Sales' }"
            class="card shadow-sm dashboard-card dashboard-summary-link h-100"
            aria-label="Voir les ventes du jour"
          >
            <div class="card-body">
              <div class="dashboard-card-content">
                <div class="position-relative">
                  <div class="small text-muted">Ventes aujourd’hui</div>
                  <div class="h4 mb-0">
                    {{ $format.currency(totals.sales_today_amount) }}
                  </div>
                  <div class="small text-muted">
                    {{ totals.sales_today_count }} ticket(s) · Panier moyen :
                    <strong>{{ $format.currency(totals.avg_basket_today) }}</strong>
                  </div>
                </div>
                <div class="dashboard-icon bg-success-subtle text-success">
                  <i class="fa fa-coins"></i>
                </div>
              </div>
            </div>
          </RouterLink>
        </div>

        <!-- Ventes période -->
        <div class="col-xl-3 col-md-6">
          <RouterLink
            :to="{ name: 'Sales' }"
            class="card shadow-sm dashboard-card dashboard-summary-link h-100"
            aria-label="Voir les ventes de la période"
          >
            <div class="card-body">
              <div class="dashboard-card-content">
                <div class="position-relative">
                  <div class="small text-muted">Ventes ({{ periodLabel }})</div>
                  <div class="h4 mb-0">
                    {{ $format.currency(totals.sales_period_amount) }}
                  </div>
                  <div class="small text-muted">
                    {{ totals.sales_period_count }} vente(s) · Panier moyen :
                    <strong>{{ $format.currency(totals.avg_basket_period) }}</strong>
                  </div>
                </div>
                <div class="dashboard-icon bg-warning-subtle text-warning">
                  <i class="fa fa-chart-line"></i>
                </div>
              </div>

              <hr class="my-2" />

              <div class="d-flex justify-content-between small">
                <span class="text-muted">Marge estimée</span>
                <strong>{{ $format.currency(totals.estimated_margin_period) }}</strong>
              </div>
            </div>
          </RouterLink>
        </div>
      </div>

      <PrimevueTabView class="dashboard-tabs mb-3">
        <PrimevueTabPanel header="Ventes">
          <div class="row g-2 mb-3">
            <div v-for="item in salesKpis" :key="item.label" class="col-xl-3 col-md-6">
              <DashboardKpiCard :item="item" />
            </div>
          </div>

          <div class="row g-3 mb-3">
            <div v-for="chart in salesCharts" :key="chart.key" class="col-xl-4 col-lg-6">
              <div class="card h-100 border-0 shadow-sm">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <div class="d-flex align-items-center gap-2">
                      <h5 class="card-title mb-0">{{ chart.title }}</h5>
                      <ChartInfoButton :text="chartInfoText(chart.key)" />
                    </div>
                    <small class="text-muted" v-if="chartPointCount(chart)">
                      {{ chartPointCount(chart) }} point(s)
                    </small>
                  </div>

                  <div v-if="!hasChartData(chart)" class="text-muted small">
                    {{ chart.empty }}
                  </div>

                  <PrimevueChart
                    v-else
                    :type="chart.type"
                    :data="chartData(chart)"
                    :options="chartOptions(chart)"
                    :height="260"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="row g-3">
            <div class="col-xl-6">
              <div class="card h-100 border-0 shadow-sm">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <div class="d-flex align-items-center gap-2">
                      <h5 class="card-title mb-0">Top produits</h5>
                      <ChartInfoButton :text="chartInfoText('topProducts')" />
                    </div>
                    <small class="text-muted">{{ topProducts.length }} produit(s)</small>
                  </div>

                  <div v-if="!topProducts?.length" class="text-muted small">Aucune donnée.</div>

                  <PrimevueChart
                    v-else
                    type="bar"
                    :data="topProductsData"
                    :options="topProductsOptions"
                    :height="320"
                  />
                </div>
              </div>
            </div>

            <div class="col-xl-6">
              <div class="card h-100 border-0 shadow-sm">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <div class="d-flex align-items-center gap-2">
                      <h5 class="card-title mb-0">Top clients</h5>
                      <ChartInfoButton :text="chartInfoText('topClients')" />
                    </div>
                    <small class="text-muted">{{ topClients.length }} client(s)</small>
                  </div>

                  <div v-if="!topClients?.length" class="text-muted small">Aucune donnée.</div>

                  <PrimevueChart
                    v-else
                    type="bar"
                    :data="topClientsData"
                    :options="topClientsOptions"
                    :height="320"
                  />
                </div>
              </div>
            </div>
          </div>
        </PrimevueTabPanel>

        <PrimevueTabPanel v-if="isExpensesEnabled" header="Dépenses d’exploitation">
          <div v-if="isLoading" class="text-center text-muted py-5" role="status">
            <i class="fa fa-spinner fa-spin me-2" aria-hidden="true"></i>Chargement des dépenses…
          </div>

          <template v-else>
            <div class="row g-2 mb-3">
              <div v-for="item in expenseKpis" :key="item.label" class="col-xl-2 col-lg-4 col-md-6">
                <DashboardKpiCard :item="item" />
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-xl-7">
                <div class="card h-100 border-0 shadow-sm">
                  <div class="card-body">
                    <div class="d-flex align-items-center gap-2 mb-2">
                      <h5 class="card-title mb-0">Évolution quotidienne</h5>
                      <ChartInfoButton :text="chartInfoText('expenseByDay')" />
                    </div>
                    <div v-if="!expenseByDay.length" class="text-muted small py-5 text-center">Aucune dépense financière sur la période.</div>
                    <PrimevueChart v-else type="line" :data="expenseByDayData" :options="expenseByDayOptions" :height="280" />
                  </div>
                </div>
              </div>

              <div class="col-xl-5">
                <div class="card h-100 border-0 shadow-sm">
                  <div class="card-body">
                    <div class="d-flex align-items-center gap-2 mb-2">
                      <h5 class="card-title mb-0">Répartition par type</h5>
                      <ChartInfoButton :text="chartInfoText('expenseByType')" />
                    </div>
                    <div v-if="!expenseByType.length" class="text-muted small py-5 text-center">Aucune répartition disponible.</div>
                    <PrimevueChart v-else type="doughnut" :data="expenseByTypeData" :options="expenseByTypeOptions" :height="280" />
                  </div>
                </div>
              </div>
            </div>

            <div class="card border-0 shadow-sm">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h5 class="card-title mb-0">Dépenses récentes</h5>
                  <RouterLink :to="{ name: 'Expenses' }" class="small">Voir toutes les dépenses</RouterLink>
                </div>
                <div class="table-responsive">
                  <table class="table table-sm table-hover align-middle mb-0">
                    <thead class="table-light"><tr><th>Date</th><th>Libellé</th><th>Type</th><th>Bénéficiaire</th><th>Statut</th><th class="text-end">Montant</th></tr></thead>
                    <tbody>
                      <tr v-if="!recentExpenses.length"><td colspan="6" class="text-center text-muted py-4">Aucune dépense récente sur la période.</td></tr>
                      <tr v-for="expense in recentExpenses" :key="expense.id">
                        <td>{{ $format.date(expense.expense_date) }}</td>
                        <td><RouterLink :to="{ name: 'ExpenseShow', params: { id: expense.id } }" class="fw-semibold text-decoration-none">{{ expense.label || expense.reference || `#${expense.id}` }}</RouterLink></td>
                        <td>{{ expenseTypeLabel(expense) }}</td>
                        <td>{{ expenseBeneficiaryLabel(expense) }}</td>
                        <td><span class="badge" :class="expenseStatusBadge(expense.status)">{{ expenseStatusText(expense.status) }}</span></td>
                        <td class="text-end">{{ $format.currency(expense.amount || 0, expense.currency_code) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </template>
        </PrimevueTabPanel>

        <PrimevueTabPanel header="Utilisateurs">
          <div class="row g-2 mb-3">
            <div v-for="item in userKpis" :key="item.label" class="col-xl col-lg-4 col-md-6">
              <DashboardKpiCard :item="item" />
            </div>
          </div>

          <div class="row g-3 mb-3">
            <div v-for="chart in userCharts" :key="chart.key" class="col-xl-6">
              <div class="card h-100 border-0 shadow-sm">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <div class="d-flex align-items-center gap-2">
                      <h5 class="card-title mb-0">{{ chart.title }}</h5>
                      <ChartInfoButton :text="chartInfoText(chart.key)" />
                    </div>
                    <small class="text-muted" v-if="chartPointCount(chart)">
                      {{ chartPointCount(chart) }} point(s)
                    </small>
                  </div>

                  <div v-if="!hasChartData(chart)" class="text-muted small">
                    {{ chart.empty }}
                  </div>

                  <PrimevueChart
                    v-else
                    :type="chart.type"
                    :data="chartData(chart)"
                    :options="chartOptions(chart)"
                    :height="260"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="card border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <div class="d-flex align-items-center gap-2">
                  <h5 class="card-title mb-0">Performance caissiers</h5>
                  <ChartInfoButton
                    :text="chartInfoText('cashierPerformance')"
                    label="Comprendre ce tableau"
                  />
                </div>
                <small class="text-muted">{{ cashierPerformance.length }} caissier(s)</small>
              </div>

              <div class="table-responsive">
                <table class="table table-sm align-middle mb-0">
                  <thead class="table-light">
                    <tr>
                      <th>Caissier</th>
                      <th class="text-end">Sessions</th>
                      <th class="text-end">Heures</th>
                      <th class="text-end">Tickets</th>
                      <th class="text-end">CA</th>
                      <th class="text-end">Panier moyen</th>
                      <th class="text-end">CA / heure</th>
                      <th class="text-end">Tickets / heure</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!cashierPerformance.length">
                      <td colspan="8" class="text-center text-muted py-3">
                        Aucun caissier actif sur la période sélectionnée.
                      </td>
                    </tr>
                    <tr v-for="row in cashierPerformance" :key="row.cashier_id">
                      <td class="fw-semibold">{{ row.cashier_name }}</td>
                      <td class="text-end">{{ numberValue(row.sessions_count) }}</td>
                      <td class="text-end">{{ hourValue(row.worked_hours) }}</td>
                      <td class="text-end">{{ numberValue(row.sales_count) }}</td>
                      <td class="text-end">{{ $format.currency(row.sales_amount || 0) }}</td>
                      <td class="text-end">{{ $format.currency(row.avg_basket || 0) }}</td>
                      <td class="text-end">{{ $format.currency(row.sales_per_hour || 0) }}</td>
                      <td class="text-end">{{ numberValue(row.tickets_per_hour, 2) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </PrimevueTabPanel>

        <PrimevueTabPanel header="Stock">
          <div class="row g-2 mb-3">
            <div v-for="item in stockKpis" :key="item.label" class="col-xl-3 col-md-6">
              <DashboardKpiCard :item="item" />
            </div>
          </div>

          <div class="row g-3">
            <div v-for="chart in stockCharts" :key="chart.key" class="col-xl-6">
              <div class="card h-100 border-0 shadow-sm">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <div class="d-flex align-items-center gap-2">
                      <h5 class="card-title mb-0">{{ chart.title }}</h5>
                      <ChartInfoButton :text="chartInfoText(chart.key)" />
                    </div>
                    <small class="text-muted" v-if="chartPointCount(chart)">
                      {{ chartPointCount(chart) }} point(s)
                    </small>
                  </div>

                  <div v-if="!hasChartData(chart)" class="text-muted small">
                    {{ chart.empty }}
                  </div>

                  <PrimevueChart
                    v-else
                    :type="chart.type"
                    :data="chartData(chart)"
                    :options="chartOptions(chart)"
                    :height="260"
                  />
                </div>
              </div>
            </div>
          </div>
        </PrimevueTabPanel>

        <PrimevueTabPanel v-if="isWalletEnabled" header="Portefeuille">
          <div class="row g-2 mb-3">
            <div v-for="item in walletKpis" :key="item.label" class="col-xl-3 col-md-6">
              <DashboardKpiCard :item="item" />
            </div>
          </div>

          <div class="row g-3">
            <div v-for="chart in walletCharts" :key="chart.key" :class="chart.columnClass">
              <div class="card h-100 border-0 shadow-sm">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <div class="d-flex align-items-center gap-2">
                      <h5 class="card-title mb-0">{{ chart.title }}</h5>
                      <ChartInfoButton :text="chartInfoText(chart.key)" />
                    </div>
                    <small v-if="chartPointCount(chart)" class="text-muted">
                      {{ chartPointCount(chart) }} point(s)
                    </small>
                  </div>

                  <div v-if="!hasChartData(chart)" class="text-muted small">
                    {{ chart.empty }}
                  </div>

                  <PrimevueChart
                    v-else
                    :type="chart.type"
                    :data="chartData(chart)"
                    :options="chartOptions(chart)"
                    :height="chart.height"
                  />
                </div>
              </div>
            </div>
          </div>
        </PrimevueTabPanel>

        <PrimevueTabPanel header="Réapprovisionnement">
          <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div>
              <h5 class="mb-1">Produits à réapprovisionner</h5>
              <p class="text-muted small mb-0">
                État actuel des produits actifs en rupture ou au seuil minimum.
              </p>
            </div>
            <div class="d-flex gap-2 flex-wrap">
              <span class="badge bg-danger">Rupture : {{ replenishmentOutOfStockCount }}</span>
              <span class="badge bg-warning text-dark">
                Seuil minimum : {{ replenishmentMinimumCount }}
              </span>
            </div>
          </div>

          <BootstrapDataTable
            :rows="replenishmentProducts"
            :columns="replenishmentColumns"
            :loading="isLoading"
            row-key="id"
            searchable
            search-placeholder="Rechercher un produit à réapprovisionner..."
            empty-text="Aucun produit à réapprovisionner."
            sort-by="stock_quantity"
            sort-dir="asc"
            state-key="dashboard.replenishment-products"
            export-file-name="produits-a-reapprovisionner.xlsx"
            export-sheet-name="Réapprovisionnement"
            print-title="Produits à réapprovisionner"
            :show-refresh="false"
          >
            <template #cell-name="{ row }">
              <RouterLink
                :to="{ name: 'ProductShow', params: { id: row.id } }"
                class="fw-semibold text-decoration-none"
              >
                {{ row.name || '—' }}
              </RouterLink>
              <small v-if="row.allow_negative_stock_sales" class="text-muted d-block">
                Vente avec stock négatif autorisée
              </small>
            </template>
            <template #cell-sku="{ row }">{{ row.sku || '—' }}</template>
            <template #cell-stock_quantity="{ row }">
              <span :class="{ 'text-danger fw-semibold': Number(row.stock_quantity || 0) <= 0 }">
                {{ Number(row.stock_quantity || 0) }}
              </span>
            </template>
            <template #cell-min_stock_quantity="{ row }">
              {{ Number(row.min_stock_quantity || 0) }}
            </template>
            <template #cell-alert_type="{ row }">
              <span
                class="badge"
                :class="row.alert_type === 'out_of_stock' ? 'bg-danger' : 'bg-warning text-dark'"
              >
                {{ row.alert_type === 'out_of_stock' ? 'Rupture' : 'Seuil minimum' }}
              </span>
            </template>
          </BootstrapDataTable>
        </PrimevueTabPanel>

        <PrimevueTabPanel header="Achats">
          <div class="row g-2 mb-3">
            <div v-for="item in purchaseKpis" :key="item.label" class="col-xl-3 col-md-6">
              <DashboardKpiCard :item="item" />
            </div>
          </div>

          <div class="row g-3">
            <div v-for="chart in purchaseCharts" :key="chart.key" class="col-xl-6">
              <div class="card h-100 border-0 shadow-sm">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <div class="d-flex align-items-center gap-2">
                      <h5 class="card-title mb-0">{{ chart.title }}</h5>
                      <ChartInfoButton :text="chartInfoText(chart.key)" />
                    </div>
                    <small class="text-muted" v-if="chartPointCount(chart)">
                      {{ chartPointCount(chart) }} point(s)
                    </small>
                  </div>

                  <div v-if="!hasChartData(chart)" class="text-muted small">
                    {{ chart.empty }}
                  </div>

                  <PrimevueChart
                    v-else
                    :type="chart.type"
                    :data="chartData(chart)"
                    :options="chartOptions(chart)"
                    :height="260"
                  />
                </div>
              </div>
            </div>
          </div>
        </PrimevueTabPanel>

        <PrimevueTabPanel header="Opérations">
          <div class="row g-2 mb-3">
            <div v-for="item in operationKpis" :key="item.label" class="col-xl-3 col-md-6">
              <DashboardKpiCard :item="item" />
            </div>
          </div>

          <div class="row g-3">
            <div v-for="chart in operationCharts" :key="chart.key" class="col-xl-6">
              <div class="card h-100 border-0 shadow-sm">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <div class="d-flex align-items-center gap-2">
                      <h5 class="card-title mb-0">{{ chart.title }}</h5>
                      <ChartInfoButton :text="chartInfoText(chart.key)" />
                    </div>
                    <small class="text-muted" v-if="chartPointCount(chart)">
                      {{ chartPointCount(chart) }} point(s)
                    </small>
                  </div>

                  <div v-if="!hasChartData(chart)" class="text-muted small">
                    {{ chart.empty }}
                  </div>

                  <PrimevueChart
                    v-else
                    :type="chart.type"
                    :data="chartData(chart)"
                    :options="chartOptions(chart)"
                    :height="260"
                  />
                </div>
              </div>
            </div>
          </div>
        </PrimevueTabPanel>

        <PrimevueTabPanel header="Expirations">
          <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div>
              <h5 class="mb-1">Lots expirés</h5>
              <p class="text-muted small mb-0">
                Dates d’expiration comprises dans la période {{ periodLabel.toLowerCase() }}.
              </p>
            </div>
            <span class="badge bg-danger-subtle text-danger border border-danger-subtle">
              {{ expiredLots.length }} lot(s)
            </span>
          </div>

          <BootstrapDataTable
            :rows="expiredLots"
            :columns="expiredLotColumns"
            :loading="isLoading"
            row-key="id"
            searchable
            search-placeholder="Rechercher un produit, SKU, lot ou achat..."
            empty-text="Aucun lot expiré pour la période sélectionnée."
            sort-by="expiry_date"
            sort-dir="desc"
            state-key="dashboard.expired-lots"
            export-file-name="lots-expires.xlsx"
            export-sheet-name="Lots expirés"
            :print-title="`Lots expirés - ${periodLabel}`"
            :show-refresh="false"
          >
            <template #cell-product_name="{ row }">
              <RouterLink
                v-if="row.product_id"
                :to="{ name: 'ProductShow', params: { id: row.product_id } }"
                class="fw-semibold text-decoration-none"
              >
                {{ row.product_name }}
              </RouterLink>
              <span v-else>{{ row.product_name || '—' }}</span>
            </template>
            <template #cell-product_sku="{ row }">
              {{ row.product_sku || '—' }}
            </template>
            <template #cell-lot_number="{ row }">
              {{ row.lot_number || '—' }}
            </template>
            <template #cell-expiry_date="{ row }">
              {{ $format.date(row.expiry_date) }}
            </template>
            <template #cell-days_expired="{ row }">
              <span class="badge bg-danger">
                {{ Number(row.days_expired) === 0 ? "Aujourd’hui" : `${row.days_expired} jour(s)` }}
              </span>
            </template>
            <template #cell-sources="{ row }">
              <span v-if="!row.sources?.length">—</span>
              <span
                v-for="source in row.sources"
                :key="source"
                class="badge me-1"
                :class="source === 'purchase' ? 'bg-primary' : 'bg-secondary'"
              >
                {{ source === 'purchase' ? 'Achat' : 'Mouvement' }}
              </span>
            </template>
            <template #cell-purchase_references="{ row }">
              {{ row.purchase_references?.length ? row.purchase_references.join(', ') : '—' }}
            </template>
          </BootstrapDataTable>
        </PrimevueTabPanel>
      </PrimevueTabView>
    </template>
  </AdminLayout>
</template>

<script>
/* eslint-disable */
import ChartInfoButton from '@/components/ChartInfoButton.vue';
import DashboardKpiCard from '@/components/DashboardKpiCard.vue';
import { expenseStatusClass, expenseStatusLabel, expenseSupplierName, expenseTypeName } from '@/support/expenses';
import { hasPermission } from '@/support/permissions';

export default {
  name: 'DashboardView',
  components: { ChartInfoButton, DashboardKpiCard },
  data() {
    return {
      isLoading: false,
      errorMessage: '',
      period: 'week',

      range: { start: null, end: null },
      previousRange: { start: null, end: null },
      comparisons: {},

      totals: {
        products_count: 0,
        products_active_count: 0,
        products_inactive_count: 0,
        stock_quantity_sum: 0,
        stock_value_sum: 0,
        out_of_stock_count: 0,
        below_min_stock_count: 0,
        wallet_balance_total: 0,
        wallet_cash_impact_period_amount: 0,
        wallet_profit_period_amount: 0,

        sales_today_count: 0,
        sales_today_amount: 0,
        sales_period_count: 0,
        sales_period_amount: 0,
        avg_basket_today: 0,
        avg_basket_period: 0,

        estimated_margin_period: 0,

        inventories_today_count: 0,
        inventories_period_count: 0,

        purchases_period_count: 0,
        purchases_period_amount: 0,
        supplier_debt_due_amount: 0,
        supplier_returns_period_count: 0,
        supplier_returns_period_amount: 0,
        supplier_payments_period_count: 0,
        supplier_payments_period_amount: 0,

        stock_movements_period_count: 0,
        stock_entries_period_quantity: 0,
        stock_exits_period_quantity: 0,
        stock_transfers_period_count: 0,

        open_cash_sessions_count: 0,
        deliveries_period_count: 0,
        deliveries_period_amount: 0,

        users_active_count: 0,
        cashiers_active_count: 0,
        cashiers_worked_count: 0,
        cashier_worked_seconds_total: 0,
        cashier_worked_hours_total: 0,
        cashier_sales_count: 0,
        cashier_sales_amount: 0,
        cashier_sales_per_hour: 0,
      },

      salesByDay: [],
      salesByCashier: [],
      salesByStatus: [],
      inventoryByDay: [],
      topProducts: [],
      topClients: [],
      purchaseByDay: [],
      purchaseByStatus: [],
      supplierReturnsByDay: [],
      supplierPaymentsByDay: [],
      stockMovementsByImpact: [],
      stockMovementsByReason: [],
      stockTransfersByStatus: [],
      cashSessionsByStatus: [],
      cashierWorkHours: [],
      cashierPerformance: [],
      deliveriesByStatus: [],
      expiredLots: [],
      replenishmentProducts: [],
      walletActivityByDay: [],
      walletTransactionsByType: [],
      walletBalances: [],
      expenses: { enabled: false, summary: {}, by_day: [], by_type: [], recent_expenses: [] },
      replenishmentColumns: [
        { key: 'name', label: 'Produit', sortable: true },
        { key: 'sku', label: 'SKU', sortable: true },
        { key: 'stock_quantity', label: 'Stock', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'min_stock_quantity', label: 'Seuil', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'alert_type', label: 'État', sortable: true },
      ],
      expiredLotColumns: [
        { key: 'product_name', label: 'Produit', sortable: true },
        { key: 'product_sku', label: 'SKU', sortable: true },
        { key: 'lot_number', label: 'Lot', sortable: true },
        { key: 'expiry_date', label: 'Expiration', sortable: true },
        { key: 'days_expired', label: 'Retard', sortable: true },
        { key: 'sources', label: 'Origine' },
        { key: 'purchase_references', label: 'Référence achat' },
      ],

      // chart objects
      salesByDayData: {},
      salesByDayOptions: {},
      salesByCashierData: {},
      salesByCashierOptions: {},
      inventoryByDayData: {},
      inventoryByDayOptions: {},
      salesByStatusData: {},
      salesByStatusOptions: {},
      topProductsData: {},
      topProductsOptions: {},
      topClientsData: {},
      topClientsOptions: {},
      purchaseByDayData: {},
      purchaseByDayOptions: {},
      purchaseByStatusData: {},
      purchaseByStatusOptions: {},
      supplierReturnsByDayData: {},
      supplierReturnsByDayOptions: {},
      supplierPaymentsByDayData: {},
      supplierPaymentsByDayOptions: {},
      stockMovementsByImpactData: {},
      stockMovementsByImpactOptions: {},
      stockMovementsByReasonData: {},
      stockMovementsByReasonOptions: {},
      stockTransfersByStatusData: {},
      stockTransfersByStatusOptions: {},
      cashSessionsByStatusData: {},
      cashSessionsByStatusOptions: {},
      cashierWorkHoursData: {},
      cashierWorkHoursOptions: {},
      cashierSalesAmountData: {},
      cashierSalesAmountOptions: {},
      cashierAvgBasketData: {},
      cashierAvgBasketOptions: {},
      cashierTicketsPerHourData: {},
      cashierTicketsPerHourOptions: {},
      deliveriesByStatusData: {},
      deliveriesByStatusOptions: {},
      walletActivityByDayData: {},
      walletActivityByDayOptions: {},
      walletTransactionsByTypeData: {},
      walletTransactionsByTypeOptions: {},
      walletBalancesData: {},
      walletBalancesOptions: {},
      expenseByDayData: {},
      expenseByDayOptions: {},
      expenseByTypeData: {},
      expenseByTypeOptions: {},
    };
  },
  computed: {
    periodLabel() {
      switch (this.period) {
        case 'day': return "Aujourd’hui";
        case 'month': return '30 derniers jours';
        case 'week':
        default: return '7 derniers jours';
      }
    },
    rangeLabel() {
      if (!this.range?.start || !this.range?.end) return '';
      // backend: "YYYY-MM-DD HH:mm:ss"
      const s = this.range.start;
      const e = this.range.end;
      return `${this.formatDateTimeShort(s)} → ${this.formatDateTimeShort(e)}`;
    },
    salesByStatusTotalCount() {
      return (this.salesByStatus || []).reduce((sum, s) => sum + (Number(s.count) || 0), 0);
    },
    isExpensesEnabled() {
      return this.$store?.getters?.['runtime/moduleEnabled']?.('expenses') !== false
        && hasPermission(this.$store?.getters?.['auth/currentUser'], 'expenses.view');
    },
    expenseSummary() {
      return this.expenses?.summary || {};
    },
    expenseByDay() {
      return this.expenses?.by_day || [];
    },
    expenseByType() {
      return this.expenses?.by_type || [];
    },
    recentExpenses() {
      return this.expenses?.recent_expenses || [];
    },
    expenseKpis() {
      return [
        this.kpi('Dépenses', this.numberValue(this.expenseSummary.expenses_count)),
        this.kpi('Montant total', this.$format.currency(this.expenseSummary.total_amount || 0)),
        this.kpi('Montant payé', this.$format.currency(this.expenseSummary.paid_amount || 0)),
        this.kpi('Reste dû', this.$format.currency(this.expenseSummary.due_amount || 0)),
        this.kpi('Échéances proches', `${this.numberValue(this.expenseSummary.due_soon_count)} · ${this.$format.currency(this.expenseSummary.due_soon_amount || 0)}`),
        this.kpi('Retards', `${this.numberValue(this.expenseSummary.overdue_count)} · ${this.$format.currency(this.expenseSummary.overdue_amount || 0)}`),
      ];
    },
    replenishmentOutOfStockCount() {
      return this.replenishmentProducts.filter((product) => product.alert_type === 'out_of_stock').length;
    },
    replenishmentMinimumCount() {
      return this.replenishmentProducts.filter((product) => product.alert_type === 'minimum_reached').length;
    },
    salesKpis() {
      return [
        this.kpi('Chiffre d’affaires', this.$format.currency(this.totals.sales_period_amount || 0), 'sales_period_amount', 'money'),
        this.kpi('Tickets', Number(this.totals.sales_period_count || 0).toLocaleString('fr-FR'), 'sales_period_count'),
        this.kpi('Panier moyen', this.$format.currency(this.totals.avg_basket_period || 0), 'avg_basket_period', 'money'),
        this.kpi('Marge estimée', this.$format.currency(this.totals.estimated_margin_period || 0), 'estimated_margin_period', 'money'),
      ];
    },
    stockKpis() {
      return [
        this.kpi('Mouvements', Number(this.totals.stock_movements_period_count || 0).toLocaleString('fr-FR'), 'stock_movements_period_count'),
        this.kpi('Entrées', Number(this.totals.stock_entries_period_quantity || 0).toLocaleString('fr-FR'), 'stock_entries_period_quantity'),
        this.kpi('Sorties', Number(this.totals.stock_exits_period_quantity || 0).toLocaleString('fr-FR'), 'stock_exits_period_quantity'),
        this.kpi('Transferts', Number(this.totals.stock_transfers_period_count || 0).toLocaleString('fr-FR'), 'stock_transfers_period_count'),
      ];
    },
    walletKpis() {
      return [
        this.kpi('Soldes portefeuilles', this.$format.currency(this.totals.wallet_balance_total || 0)),
        this.kpi('Transactions portefeuille', this.$format.currency(this.totals.wallet_cash_impact_period_amount || 0)),
        this.kpi('Marge estimée portefeuille', this.$format.currency(this.totals.wallet_profit_period_amount || 0)),
      ];
    },
    isWalletEnabled() {
      return this.$store?.getters?.['runtime/moduleEnabled']?.('wallet') !== false;
    },
    walletCharts() {
      return [
        {
          key: 'walletActivityByDay',
          title: `Activité quotidienne (${this.periodLabel})`,
          type: 'bar',
          collectionKey: 'walletActivityByDay',
          dataKey: 'walletActivityByDayData',
          optionsKey: 'walletActivityByDayOptions',
          empty: 'Aucune opération portefeuille sur la période.',
          columnClass: 'col-12',
          height: 300,
        },
        {
          key: 'walletTransactionsByType',
          title: `Répartition des opérations (${this.periodLabel})`,
          type: 'doughnut',
          collectionKey: 'walletTransactionsByType',
          dataKey: 'walletTransactionsByTypeData',
          optionsKey: 'walletTransactionsByTypeOptions',
          empty: 'Aucune opération portefeuille sur la période.',
          columnClass: 'col-xl-6',
          height: 300,
        },
        {
          key: 'walletBalances',
          title: 'Soldes actuels par portefeuille',
          type: 'bar',
          collectionKey: 'walletBalances',
          dataKey: 'walletBalancesData',
          optionsKey: 'walletBalancesOptions',
          empty: 'Aucun portefeuille actif.',
          columnClass: 'col-xl-6',
          height: 300,
        },
      ];
    },
    purchaseKpis() {
      return [
        this.kpi('Achats', Number(this.totals.purchases_period_count || 0).toLocaleString('fr-FR'), 'purchases_period_count'),
        this.kpi('Montant achats', this.$format.currency(this.totals.purchases_period_amount || 0), 'purchases_period_amount', 'money'),
        this.kpi('Dette fournisseur', this.$format.currency(this.totals.supplier_debt_due_amount || 0)),
        this.kpi('Règlements', this.$format.currency(this.totals.supplier_payments_period_amount || 0), 'supplier_payments_period_amount', 'money'),
      ];
    },
    operationKpis() {
      const kpis = [
        this.kpi('Sessions ouvertes', Number(this.totals.open_cash_sessions_count || 0).toLocaleString('fr-FR')),
        this.kpi('Retours fournisseur', Number(this.totals.supplier_returns_period_count || 0).toLocaleString('fr-FR'), 'supplier_returns_period_count'),
      ];

      if (this.$store.getters['runtime/moduleEnabled']('ecommerce')) {
        kpis.splice(1, 0,
          this.kpi('Livraisons', Number(this.totals.deliveries_period_count || 0).toLocaleString('fr-FR'), 'deliveries_period_count'),
          this.kpi('Frais livraison', this.$format.currency(this.totals.deliveries_period_amount || 0), 'deliveries_period_amount', 'money')
        );
      }

      return kpis;
    },
    userKpis() {
      return [
        this.kpi('Utilisateurs actifs', Number(this.totals.users_active_count || 0).toLocaleString('fr-FR')),
        this.kpi('Caissiers actifs', Number(this.totals.cashiers_active_count || 0).toLocaleString('fr-FR')),
        this.kpi('Caissiers ayant travaillé', Number(this.totals.cashiers_worked_count || 0).toLocaleString('fr-FR'), 'cashiers_worked_count'),
        this.kpi('Heures travaillées', this.hourValue(this.totals.cashier_worked_hours_total || 0), 'cashier_worked_hours_total', 'hours'),
        this.kpi('CA / heure', this.$format.currency(this.totals.cashier_sales_per_hour || 0), 'cashier_sales_per_hour', 'money'),
      ];
    },
    salesCharts() {
      return [
        {
          key: 'salesByDay',
          title: `Ventes par jour (${this.periodLabel})`,
          type: 'line',
          collectionKey: 'salesByDay',
          dataKey: 'salesByDayData',
          optionsKey: 'salesByDayOptions',
          empty: 'Aucune vente validée sur la période.',
        },
        {
          key: 'salesByCashier',
          title: `Ventes par caissier (${this.periodLabel})`,
          type: 'bar',
          collectionKey: 'salesByCashier',
          dataKey: 'salesByCashierData',
          optionsKey: 'salesByCashierOptions',
          empty: 'Aucune donnée caissier sur la période.',
        },
        {
          key: 'salesByStatus',
          title: `Ventes par statut (${this.periodLabel})`,
          type: 'doughnut',
          collectionKey: 'salesByStatus',
          dataKey: 'salesByStatusData',
          optionsKey: 'salesByStatusOptions',
          empty: 'Aucune vente sur la période.',
        },
      ];
    },
    stockCharts() {
      return [
        {
          key: 'inventoryByDay',
          title: `Inventaires par jour (${this.periodLabel})`,
          type: 'bar',
          collectionKey: 'inventoryByDay',
          dataKey: 'inventoryByDayData',
          optionsKey: 'inventoryByDayOptions',
          empty: 'Aucun inventaire sur la période.',
        },
        {
          key: 'stockMovementsByImpact',
          title: `Entrées et sorties (${this.periodLabel})`,
          type: 'doughnut',
          collectionKey: 'stockMovementsByImpact',
          dataKey: 'stockMovementsByImpactData',
          optionsKey: 'stockMovementsByImpactOptions',
          empty: 'Aucun mouvement de stock sur la période.',
        },
        {
          key: 'stockMovementsByReason',
          title: `Mouvements par motif (${this.periodLabel})`,
          type: 'bar',
          collectionKey: 'stockMovementsByReason',
          dataKey: 'stockMovementsByReasonData',
          optionsKey: 'stockMovementsByReasonOptions',
          empty: 'Aucun motif de mouvement utilisé sur la période.',
        },
      ];
    },
    purchaseCharts() {
      return [
        {
          key: 'purchaseByDay',
          title: `Achats par jour (${this.periodLabel})`,
          type: 'line',
          collectionKey: 'purchaseByDay',
          dataKey: 'purchaseByDayData',
          optionsKey: 'purchaseByDayOptions',
          empty: 'Aucun achat sur la période.',
        },
        {
          key: 'purchaseByStatus',
          title: `Achats par statut (${this.periodLabel})`,
          type: 'doughnut',
          collectionKey: 'purchaseByStatus',
          dataKey: 'purchaseByStatusData',
          optionsKey: 'purchaseByStatusOptions',
          empty: 'Aucun statut d’achat sur la période.',
        },
        {
          key: 'supplierReturnsByDay',
          title: `Retours fournisseur (${this.periodLabel})`,
          type: 'bar',
          collectionKey: 'supplierReturnsByDay',
          dataKey: 'supplierReturnsByDayData',
          optionsKey: 'supplierReturnsByDayOptions',
          empty: 'Aucun retour fournisseur sur la période.',
        },
        {
          key: 'supplierPaymentsByDay',
          title: `Paiements fournisseur (${this.periodLabel})`,
          type: 'bar',
          collectionKey: 'supplierPaymentsByDay',
          dataKey: 'supplierPaymentsByDayData',
          optionsKey: 'supplierPaymentsByDayOptions',
          empty: 'Aucun paiement fournisseur sur la période.',
        },
      ];
    },
    operationCharts() {
      const charts = [
        {
          key: 'stockTransfersByStatus',
          title: `Transferts de stock (${this.periodLabel})`,
          type: 'doughnut',
          collectionKey: 'stockTransfersByStatus',
          dataKey: 'stockTransfersByStatusData',
          optionsKey: 'stockTransfersByStatusOptions',
          empty: 'Aucun transfert sur la période.',
        },
        {
          key: 'cashSessionsByStatus',
          title: `Sessions de caisse (${this.periodLabel})`,
          type: 'doughnut',
          collectionKey: 'cashSessionsByStatus',
          dataKey: 'cashSessionsByStatusData',
          optionsKey: 'cashSessionsByStatusOptions',
          empty: 'Aucune session de caisse sur la période.',
        },
      ];

      if (this.$store.getters['runtime/moduleEnabled']('ecommerce')) {
        charts.push({
          key: 'deliveriesByStatus',
          title: `Livraisons par statut (${this.periodLabel})`,
          type: 'doughnut',
          collectionKey: 'deliveriesByStatus',
          dataKey: 'deliveriesByStatusData',
          optionsKey: 'deliveriesByStatusOptions',
          empty: 'Aucune livraison sur la période.',
        });
      }

      return charts;
    },
    userCharts() {
      return [
        {
          key: 'cashierWorkHours',
          title: `Heures travaillées par caissier (${this.periodLabel})`,
          type: 'bar',
          collectionKey: 'cashierWorkHours',
          dataKey: 'cashierWorkHoursData',
          optionsKey: 'cashierWorkHoursOptions',
          empty: 'Aucun caissier n’a travaillé sur la période.',
        },
        {
          key: 'cashierSalesAmount',
          title: `Chiffre d’affaires par caissier (${this.periodLabel})`,
          type: 'bar',
          collectionKey: 'cashierPerformance',
          dataKey: 'cashierSalesAmountData',
          optionsKey: 'cashierSalesAmountOptions',
          empty: 'Aucune vente validée par caissier sur la période.',
        },
        {
          key: 'cashierAvgBasket',
          title: `Panier moyen par caissier (${this.periodLabel})`,
          type: 'bar',
          collectionKey: 'cashierPerformance',
          dataKey: 'cashierAvgBasketData',
          optionsKey: 'cashierAvgBasketOptions',
          empty: 'Aucun panier moyen disponible sur la période.',
        },
        {
          key: 'cashierTicketsPerHour',
          title: `Tickets par heure (${this.periodLabel})`,
          type: 'bar',
          collectionKey: 'cashierPerformance',
          dataKey: 'cashierTicketsPerHourData',
          optionsKey: 'cashierTicketsPerHourOptions',
          empty: 'Aucun ratio ticket/heure disponible sur la période.',
        },
      ];
    },
  },
  mounted() {
    this.fetchSummary();
  },
  methods: {
    async fetchSummary() {
      this.isLoading = true;
      this.errorMessage = '';

      try {
        const res = await this.$api.get('/dashboard/summary', {
          params: { period: this.period },
        });

        const data = res.data.data || {};

        this.range = data.range || { start: null, end: null };
        this.previousRange = data.previous_range || { start: null, end: null };
        this.comparisons = data.comparisons || {};

        this.totals = {
          ...this.totals,
          ...(data.totals || {}),
        };

        this.salesByDay = data.salesByDay || [];
        this.salesByCashier = data.salesByCashier || [];
        this.salesByStatus = data.salesByStatus || [];
        this.inventoryByDay = data.inventoryByDay || [];
        this.topProducts = data.topProducts || [];
        this.topClients = data.topClients || [];
        this.purchaseByDay = data.purchaseByDay || [];
        this.purchaseByStatus = data.purchaseByStatus || [];
        this.supplierReturnsByDay = data.supplierReturnsByDay || [];
        this.supplierPaymentsByDay = data.supplierPaymentsByDay || [];
        this.stockMovementsByImpact = data.stockMovementsByImpact || [];
        this.stockMovementsByReason = data.stockMovementsByReason || [];
        this.stockTransfersByStatus = data.stockTransfersByStatus || [];
        this.cashSessionsByStatus = data.cashSessionsByStatus || [];
        this.cashierWorkHours = data.cashierWorkHours || [];
        this.cashierPerformance = data.cashierPerformance || [];
        this.deliveriesByStatus = data.deliveriesByStatus || [];
        this.expiredLots = data.expiredLots || [];
        this.replenishmentProducts = data.replenishmentProducts || [];
        this.walletActivityByDay = data.walletActivityByDay || [];
        this.walletTransactionsByType = data.walletTransactionsByType || [];
        this.walletBalances = data.walletBalances || [];
        this.expenses = data.expenses || { enabled: false, summary: {}, by_day: [], by_type: [], recent_expenses: [] };

        this.buildCharts();
      } catch (e) {
        console.error('Erreur dashboard summary', e);
        this.errorMessage = "Impossible de charger les données du tableau de bord.";
      } finally {
        this.isLoading = false;
      }
    },

    kpi(label, value, comparisonKey = null, format = 'number') {
      const comparison = comparisonKey ? this.comparisons[comparisonKey] : null;

      if (!comparison) return { label, value, comparison: null, trendState: 'current' };

      const delta = Number(comparison.delta || 0);
      const trendState = delta > 0 ? 'up' : (delta < 0 ? 'down' : 'stable');
      const sign = delta > 0 ? '+' : (delta < 0 ? '−' : '');
      const absoluteDelta = Math.abs(delta);
      let formattedDelta;

      if (format === 'money') formattedDelta = this.$format.currency(absoluteDelta);
      else if (format === 'hours') formattedDelta = `${absoluteDelta.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} h`;
      else formattedDelta = absoluteDelta.toLocaleString('fr-FR', { maximumFractionDigits: 2 });

      const trendLabel = comparison.percent === null
        ? 'Nouveau'
        : `${Number(comparison.percent) > 0 ? '+' : ''}${Number(comparison.percent).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`;
      const deltaLabel = `${sign}${formattedDelta}`;
      const accessibleTrend = `${label} : ${trendLabel}, écart ${deltaLabel} par rapport à la période précédente.`;

      return {
        label,
        value,
        comparison,
        trendState,
        trendLabel,
        deltaLabel,
        accessibleTrend,
      };
    },

    buildCharts() {
      const datesSet = new Set();
      (this.salesByDay || []).forEach((d) => datesSet.add(d.date));
      (this.inventoryByDay || []).forEach((d) => datesSet.add(d.date));
      (this.purchaseByDay || []).forEach((d) => datesSet.add(d.date));
      (this.supplierReturnsByDay || []).forEach((d) => datesSet.add(d.date));
      (this.supplierPaymentsByDay || []).forEach((d) => datesSet.add(d.date));
      const dates = Array.from(datesSet).sort();

      this.salesByDayData = this.daySeriesChart(dates, this.salesByDay, 'Montant des ventes', 'total_amount', '#198754');
      this.salesByDayOptions = this.moneyChartOptions();

      this.salesByCashierData = this.categoryChart(
        this.salesByCashier,
        'cashier_name',
        'total_amount',
        'Montant vendu',
        '#0d6efd'
      );
      this.salesByCashierOptions = this.numberChartOptions({ horizontal: true });

      this.inventoryByDayData = this.daySeriesChart(dates, this.inventoryByDay, 'Inventaires', 'count', '#6f42c1');
      this.inventoryByDayOptions = this.numberChartOptions({ integer: true });

      this.salesByStatusData = this.doughnutChart(
        this.salesByStatus,
        'status',
        'count',
        (status) => this.statusLabel(status)
      );
      this.salesByStatusOptions = this.doughnutOptions();

      this.topProductsData = this.categoryChart(
        this.topProducts,
        'product_name',
        'amount',
        'Montant vendu',
        '#198754',
        (row) => row.product_name || `#${row.product_id}`
      );
      this.topProductsOptions = this.moneyCategoryChartOptions({
        horizontal: true,
        detailFormatter: (index) => `Quantité vendue : ${Number(this.topProducts[index]?.qty || 0).toLocaleString('fr-FR')}`,
      });

      this.topClientsData = this.categoryChart(
        this.topClients,
        'client_name',
        'amount',
        'Montant vendu',
        '#0d6efd',
        (row) => row.client_name || 'Comptant'
      );
      this.topClientsOptions = this.moneyCategoryChartOptions({
        horizontal: true,
        detailFormatter: (index) => `${Number(this.topClients[index]?.count || 0).toLocaleString('fr-FR')} ticket(s)`,
      });

      this.purchaseByDayData = this.daySeriesChart(dates, this.purchaseByDay, 'Montant des achats', 'total_amount', '#fd7e14');
      this.purchaseByDayOptions = this.moneyChartOptions();

      this.purchaseByStatusData = this.doughnutChart(
        this.purchaseByStatus,
        'status',
        'count',
        (status) => this.statusLabel(status)
      );
      this.purchaseByStatusOptions = this.doughnutOptions();

      this.supplierReturnsByDayData = this.daySeriesChart(
        dates,
        this.supplierReturnsByDay,
        'Retours fournisseur',
        'total_amount',
        '#dc3545'
      );
      this.supplierReturnsByDayOptions = this.moneyChartOptions();

      this.supplierPaymentsByDayData = this.daySeriesChart(
        dates,
        this.supplierPaymentsByDay,
        'Paiements fournisseur',
        'total_amount',
        '#20c997'
      );
      this.supplierPaymentsByDayOptions = this.moneyChartOptions();

      this.stockMovementsByImpactData = this.doughnutChart(
        this.stockMovementsByImpact,
        'impact',
        'quantity',
        (impact) => this.impactLabel(impact),
        ['#198754', '#dc3545']
      );
      this.stockMovementsByImpactOptions = this.doughnutOptions();

      this.stockMovementsByReasonData = this.categoryChart(
        this.stockMovementsByReason,
        'reason_label',
        'quantity',
        'Quantité mouvementée',
        '#0dcaf0'
      );
      this.stockMovementsByReasonOptions = this.numberChartOptions({ horizontal: true, integer: true });

      this.stockTransfersByStatusData = this.doughnutChart(
        this.stockTransfersByStatus,
        'status',
        'count',
        (status) => this.statusLabel(status)
      );
      this.stockTransfersByStatusOptions = this.doughnutOptions();

      this.cashSessionsByStatusData = this.doughnutChart(
        this.cashSessionsByStatus,
        'status',
        'count',
        (status) => this.statusLabel(status)
      );
      this.cashSessionsByStatusOptions = this.doughnutOptions();

      this.cashierWorkHoursData = this.categoryChart(
        this.cashierWorkHours,
        'cashier_name',
        'worked_hours',
        'Heures travaillées',
        '#6f42c1'
      );
      this.cashierWorkHoursOptions = this.hoursChartOptions({
        horizontal: true,
        detailFormatter: (index) => `${Number(this.cashierWorkHours[index]?.sessions_count || 0).toLocaleString('fr-FR')} session(s)`,
      });

      this.cashierSalesAmountData = this.categoryChart(
        this.cashierPerformance,
        'cashier_name',
        'sales_amount',
        'Chiffre d’affaires',
        '#198754'
      );
      this.cashierSalesAmountOptions = this.moneyCategoryChartOptions({
        horizontal: true,
        detailFormatter: (index) => `${Number(this.cashierPerformance[index]?.sales_count || 0).toLocaleString('fr-FR')} ticket(s)`,
      });

      this.cashierAvgBasketData = this.categoryChart(
        this.cashierPerformance,
        'cashier_name',
        'avg_basket',
        'Panier moyen',
        '#0d6efd'
      );
      this.cashierAvgBasketOptions = this.moneyCategoryChartOptions({
        horizontal: true,
        detailFormatter: (index) => `${this.hourValue(this.cashierPerformance[index]?.worked_hours || 0)} travaillées`,
      });

      this.cashierTicketsPerHourData = this.categoryChart(
        this.cashierPerformance,
        'cashier_name',
        'tickets_per_hour',
        'Tickets par heure',
        '#fd7e14'
      );
      this.cashierTicketsPerHourOptions = this.numberChartOptions({ horizontal: true });

      this.deliveriesByStatusData = this.doughnutChart(
        this.deliveriesByStatus,
        'status',
        'count',
        (status) => this.statusLabel(status)
      );
      this.deliveriesByStatusOptions = this.doughnutOptions();

      this.walletActivityByDayData = this.walletActivityChart(this.walletActivityByDay);
      this.walletActivityByDayOptions = this.walletActivityChartOptions();

      this.walletTransactionsByTypeData = this.doughnutChart(
        this.walletTransactionsByType,
        'type',
        'total_amount',
        (type) => this.walletOperationLabel(type),
        this.walletTransactionsByType.map((row) => this.walletOperationColor(row.type))
      );
      this.walletTransactionsByTypeOptions = this.doughnutOptions();

      this.walletBalancesData = this.categoryChart(
        this.walletBalances,
        'name',
        'current_balance',
        'Solde actuel',
        '#0d6efd',
        (row) => row.code ? `${row.name} (${row.code})` : row.name
      );
      this.walletBalancesOptions = this.moneyCategoryChartOptions({ horizontal: true });

      const expenseDates = this.expenseByDay.map((row) => row.date).filter(Boolean).sort();
      this.expenseByDayData = this.daySeriesChart(
        expenseDates,
        this.expenseByDay,
        'Montant des dépenses',
        'total_amount',
        '#dc3545'
      );
      this.expenseByDayOptions = this.moneyChartOptions();
      this.expenseByTypeData = this.doughnutChart(
        this.expenseByType,
        'name',
        'total_amount',
        (name) => name || 'Type non renseigné'
      );
      this.expenseByTypeOptions = this.doughnutOptions();
    },

    chartInfoText(key) {
      const infos = {
        salesByDay: 'Montant total des ventes validées regroupé par jour sur la période sélectionnée.',
        salesByCashier: 'Montant des ventes validées regroupé par caissier pour comparer la contribution de chaque utilisateur.',
        topProducts: 'Classement des produits qui génèrent le plus de chiffre d’affaires sur la période sélectionnée. Le tooltip affiche aussi la quantité vendue.',
        topClients: 'Classement des clients qui génèrent le plus de chiffre d’affaires sur la période sélectionnée. Le tooltip affiche aussi le nombre de tickets.',
        inventoryByDay: 'Nombre d’inventaires créés par jour afin de suivre le rythme de contrôle du stock.',
        salesByStatus: 'Répartition des ventes par statut pour distinguer les brouillons, ventes validées et annulations.',
        purchaseByDay: 'Montant des commandes d’achat enregistrées par jour. Il permet de repérer les périodes où le réapprovisionnement coûte le plus.',
        purchaseByStatus: 'Répartition des achats par statut pour suivre les brouillons, validations et annulations.',
        supplierReturnsByDay: 'Montant des retours fournisseur par jour. Une hausse peut signaler un problème de qualité ou d’approvisionnement.',
        supplierPaymentsByDay: 'Montant des règlements fournisseur par jour afin de suivre les sorties de trésorerie liées aux achats.',
        stockMovementsByImpact: 'Comparaison des quantités entrées et sorties du stock, tous motifs confondus, sur la période sélectionnée.',
        stockMovementsByReason: 'Quantités mouvementées par motif. Ce graphique aide à comprendre pourquoi le stock augmente ou diminue.',
        stockTransfersByStatus: 'Répartition des transferts entre sites par statut: brouillon, envoyé, reçu ou annulé.',
        cashSessionsByStatus: 'Répartition des sessions de caisse ouvertes et fermées sur la période.',
        cashierWorkHours: 'Nombre d’heures réellement travaillées par caissier sur la période sélectionnée. Les sessions ouvertes sont comptées jusqu’à maintenant et seuls les caissiers ayant travaillé sont affichés.',
        cashierSalesAmount: 'Chiffre d’affaires des ventes validées créées par chaque caissier sur la période sélectionnée.',
        cashierAvgBasket: 'Montant moyen d’un ticket validé par caissier. Il aide à comparer la valeur moyenne des ventes.',
        cashierTicketsPerHour: 'Nombre moyen de tickets validés par heure travaillée. Les heures viennent des sessions de caisse.',
        cashierPerformance: 'Synthèse croisée des heures travaillées, sessions, ventes validées, panier moyen et productivité horaire par caissier.',
        deliveriesByStatus: 'Répartition des livraisons par statut pour identifier les livraisons en attente, assignées, livrées ou annulées.',
        walletActivityByDay: 'Montants quotidiens des opérations validées par type. La courbe représente le nombre total d’écritures de portefeuille.',
        walletTransactionsByType: 'Répartition des montants validés entre recharges, dépôts, retraits, ajustements et annulations sur la période.',
        walletBalances: 'Solde disponible actuel de chaque portefeuille électronique actif du site connecté.',
        expenseByDay: 'Montant des dépenses validées regroupé par date de dépense. Les brouillons et annulations sont exclus.',
        expenseByType: 'Répartition des dépenses validées par type. Les brouillons et annulations sont exclus.',
      };

      return infos[key] || '';
    },

    chartData(chart) {
      return this[chart.dataKey] || {};
    },

    chartOptions(chart) {
      return this[chart.optionsKey] || {};
    },

    chartPointCount(chart) {
      return (this[chart.collectionKey] || []).length;
    },

    hasChartData(chart) {
      return this.chartPointCount(chart) > 0;
    },

    daySeriesChart(dates, rows, label, valueKey, color) {
      const map = {};
      (rows || []).forEach((row) => {
        map[row.date] = Number(row[valueKey] || 0);
      });

      return {
        labels: dates.map((date) => this.formatDateShort(date)),
        datasets: [
          {
            label,
            data: dates.map((date) => map[date] || 0),
            borderColor: color,
            backgroundColor: color,
            tension: 0.3,
            fill: false,
          },
        ],
      };
    },

    walletOperationLabel(type) {
      return ({
        recharge: 'Recharge',
        deposit: 'Dépôt',
        withdrawal: 'Retrait',
        adjustment: 'Ajustement',
        cancellation: 'Annulation',
      })[type] || type || 'Inconnu';
    },

    walletOperationColor(type) {
      return ({
        recharge: '#198754',
        deposit: '#0d6efd',
        withdrawal: '#fd7e14',
        adjustment: '#6f42c1',
        cancellation: '#dc3545',
      })[type] || '#6c757d';
    },

    walletActivityChart(rows) {
      const labels = (rows || []).map((row) => this.formatDateShort(row.date));
      const amountDatasets = [
        ['recharge', 'Recharge', 'recharges_amount'],
        ['deposit', 'Dépôt', 'deposits_amount'],
        ['withdrawal', 'Retrait', 'withdrawals_amount'],
        ['adjustment', 'Ajustement', 'adjustments_amount'],
        ['cancellation', 'Annulation', 'cancellations_amount'],
      ].map(([type, label, key]) => ({
        type: 'bar',
        label,
        data: (rows || []).map((row) => Number(row[key] || 0)),
        backgroundColor: this.walletOperationColor(type),
        borderColor: this.walletOperationColor(type),
        yAxisID: 'amount',
      }));

      return {
        labels,
        datasets: [
          ...amountDatasets,
          {
            type: 'line',
            label: 'Nombre d’opérations',
            data: (rows || []).map((row) => Number(row.transactions_count || 0)),
            borderColor: '#212529',
            backgroundColor: '#212529',
            tension: 0.3,
            fill: false,
            yAxisID: 'transactions',
          },
        ],
      };
    },

    walletActivityChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: true, position: 'bottom' },
          tooltip: {
            callbacks: {
              label: (ctx) => ctx.dataset.yAxisID === 'transactions'
                ? `${ctx.dataset.label} : ${Number(ctx.parsed.y || 0).toLocaleString('fr-FR')}`
                : `${ctx.dataset.label} : ${this.$format.currency(ctx.parsed.y || 0)}`,
            },
          },
        },
        scales: {
          amount: {
            beginAtZero: true,
            position: 'left',
            ticks: { callback: (value) => this.$format.currency(value || 0) },
          },
          transactions: {
            beginAtZero: true,
            position: 'right',
            grid: { drawOnChartArea: false },
            ticks: { precision: 0 },
          },
        },
      };
    },

    categoryChart(rows, labelKey, valueKey, label, color, labelFormatter = null) {
      return {
        labels: (rows || []).map((row) => labelFormatter ? labelFormatter(row) : (row[labelKey] || 'Non renseigné')),
        datasets: [
          {
            label,
            data: (rows || []).map((row) => Number(row[valueKey] || 0)),
            backgroundColor: color,
            borderColor: color,
          },
        ],
      };
    },

    doughnutChart(rows, labelKey, valueKey, labelFormatter, colors = null) {
      return {
        labels: (rows || []).map((row) => labelFormatter(row[labelKey])),
        datasets: [
          {
            data: (rows || []).map((row) => Number(row[valueKey] || 0)),
            backgroundColor: colors || ['#0d6efd', '#198754', '#ffc107', '#dc3545', '#6f42c1', '#20c997'],
          },
        ],
      };
    },

    numberChartOptions({ horizontal = false, integer = false } = {}) {
      return {
        indexAxis: horizontal ? 'y' : 'x',
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { beginAtZero: true, ticks: integer ? { precision: 0 } : {} },
          y: { beginAtZero: true, ticks: integer ? { precision: 0 } : {} },
        },
      };
    },

    moneyChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: true },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.dataset.label || 'Montant'} : ${this.$format.currency(ctx.parsed.y ?? ctx.parsed ?? 0)}`,
            },
          },
        },
        scales: { y: { beginAtZero: true } },
      };
    },

    moneyCategoryChartOptions({ horizontal = false, detailFormatter = null } = {}) {
      return {
        indexAxis: horizontal ? 'y' : 'x',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const value = horizontal ? ctx.parsed.x : ctx.parsed.y;
                const parts = [`${ctx.dataset.label || 'Montant'} : ${this.$format.currency(value || 0)}`];
                if (detailFormatter) {
                  parts.push(detailFormatter(ctx.dataIndex));
                }
                return parts;
              },
            },
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            ticks: {
              callback: (value) => this.$format.currency(value || 0),
            },
          },
          y: {
            beginAtZero: true,
            ticks: {
              autoSkip: false,
            },
          },
        },
      };
    },

    hoursChartOptions({ horizontal = false, detailFormatter = null } = {}) {
      return {
        indexAxis: horizontal ? 'y' : 'x',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const value = horizontal ? ctx.parsed.x : ctx.parsed.y;
                const parts = [`${ctx.dataset.label || 'Heures'} : ${Number(value || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} h`];
                if (detailFormatter) {
                  parts.push(detailFormatter(ctx.dataIndex));
                }
                return parts;
              },
            },
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            ticks: {
              callback: (value) => `${Number(value || 0).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} h`,
            },
          },
          y: {
            beginAtZero: true,
            ticks: {
              autoSkip: false,
            },
          },
        },
      };
    },

    doughnutOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: true, position: 'bottom' },
        },
      };
    },

    // ===== Helpers =====
    formatDateShort(dateStr) {
      // dateStr = YYYY-MM-DD
      const parts = String(dateStr || '').split('-');
      if (parts.length !== 3) return dateStr;
      const [y, m, d] = parts;
      return `${d}/${m}`;
    },

    formatDateTimeShort(dateTimeStr) {
      // dateTimeStr = YYYY-MM-DD HH:mm:ss
      if (!dateTimeStr) return '';
      const [datePart, timePart] = String(dateTimeStr).split(' ');
      if (!datePart) return dateTimeStr;
      const shortDate = this.formatDateShort(datePart);
      const shortTime = timePart ? timePart.slice(0, 5) : '';
      return shortTime ? `${shortDate} ${shortTime}` : shortDate;
    },

    statusLabel(status) {
      switch (status) {
        case 'draft': return 'Brouillon';
        case 'closed': return 'Clos';
        case 'validated': return 'Validée';
        case 'cancelled': return 'Annulée';
        case 'abandoned': return 'Abandonnée';
        case 'sent': return 'Envoyé';
        case 'received': return 'Reçu';
        case 'open': return 'Ouverte';
        case 'pending': return 'En attente';
        case 'assigned': return 'Assignée';
        case 'delivered': return 'Livrée';
        default: return status || '—';
      }
    },

    expenseStatusText(status) {
      return expenseStatusLabel(status);
    },

    expenseStatusBadge(status) {
      return expenseStatusClass(status);
    },

    expenseTypeLabel(expense) {
      return expenseTypeName(expense);
    },

    expenseBeneficiaryLabel(expense) {
      return expenseSupplierName(expense);
    },

    impactLabel(impact) {
      return impact === 'entry' ? 'Entrées' : 'Sorties';
    },

    numberValue(value, maximumFractionDigits = 0) {
      return Number(value || 0).toLocaleString('fr-FR', { maximumFractionDigits });
    },

    hourValue(value) {
      return `${Number(value || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} h`;
    },
  },
};
</script>

<style scoped>
.dashboard-card {
  border: 1px solid transparent;
  border-radius: 0.75rem;
  color: inherit;
  overflow: hidden;
  position: relative;
  text-decoration: none;
  transition: border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease;
}

.dashboard-card-content {
  padding-right: 4.5rem;
}

.dashboard-card .card-body {
  position: relative;
}

.dashboard-summary-link:hover,
.dashboard-summary-link:focus-visible {
  border-color: var(--suite241-admin-primary, var(--bs-primary));
  box-shadow: 0 0.8rem 1.75rem rgba(var(--suite241-admin-primary-rgb, 13, 110, 253), 0.18) !important;
  color: inherit;
  outline: 0;
  text-decoration: none;
  transform: translateY(-4px);
}

.dashboard-summary-link:focus-visible {
  box-shadow: 0 0 0 3px rgba(var(--suite241-admin-primary-rgb, 13, 110, 253), 0.22),
    0 0.8rem 1.75rem rgba(var(--suite241-admin-primary-rgb, 13, 110, 253), 0.18) !important;
}

.dashboard-icon {
  align-items: center;
  background: transparent !important;
  display: inline-flex;
  font-size: 4rem;
  justify-content: center;
  opacity: 0.16;
  pointer-events: none;
  position: absolute;
  right: 1rem;
  top: 0.85rem;
  transform: translateX(0) scale(1);
  transition: opacity 180ms ease, transform 180ms ease;
  width: 4.5rem;
  z-index: 0;
}

.dashboard-summary-link:hover .dashboard-icon,
.dashboard-summary-link:focus-visible .dashboard-icon {
  opacity: 0.26;
  transform: translateX(-4px) scale(1.1);
}

@media (prefers-reduced-motion: reduce) {
  .dashboard-card,
  .dashboard-icon {
    transition: none;
  }

  .dashboard-summary-link:hover,
  .dashboard-summary-link:focus-visible,
  .dashboard-summary-link:hover .dashboard-icon,
  .dashboard-summary-link:focus-visible .dashboard-icon {
    transform: none;
  }
}

.bg-primary-subtle { background-color: rgba(13, 110, 253, 0.08); }
.bg-success-subtle { background-color: rgba(25, 135, 84, 0.08); }
.bg-warning-subtle { background-color: rgba(255, 193, 7, 0.10); }
.bg-info-subtle { background-color: rgba(13, 202, 240, 0.10); }
.bg-danger-subtle { background-color: rgba(220, 53, 69, 0.10); }

.dashboard-tabs :deep(.p-tabview-nav-link) {
  font-size: 0.88rem;
  font-weight: 700;
  padding: 0.85rem 1rem;
}

.dashboard-tabs :deep(.p-tabview-panels) {
  background: transparent;
  padding: 1rem 0 0;
}

</style>
